import isFinite from 'lodash/isFinite'
import { getConfigDataServer } from 'services/config'
import { isLoggedIn } from 'services/auth'
import { isPageReady } from 'services/pages'
import { SET_PAGE_READY } from 'services/pages/redux/actions'
import { SET_AUTH_DATA } from 'services/auth/redux/actions'
import {
  RESOLVE_USER_ABUSE_REPORT,
  GET_USER_ABUSE_ACCUSED_REPORTS,
  GET_USER_ABUSE_ACCUSER_REPORTS,
  RESOLVE_USER_ABUSE_ACCUSER_REPORT,
  RESOLVE_USER_ABUSE_ACCUSED_REPORT,
  GET_USER_ABUSE_UNRESOLVED_COUNT,
  GET_USER_ABUSE_REPORTS,
  setUserAbuseReports as setUserAbuseReportsAction,
  setUserAbuseReportsError as setUserAbuseReportsErrorAction,
  setUserAbuseAccusedReports as setUserAbuseAccusedReportsAction,
  setUserAbuseAccuserReports as setUserAbuseAccuserReportsAction,
  setUserAbuseReport as setUserAbuseReportAction,
  setUserAbuseAccusedReport as setUserAbuseAccusedReportAction,
  setUserAbuseAccuserReport as setUserAbuseAccuserReportAction,
  setUserAbuseUnresolvedCount as setUserAbuseUnresolvedCountAction,
  getUserAbuseReports as getUserAbuseReportsAction,
  setUserAbuseProcessing as setUserAbuseProcessingAction,
  setUserAbuseUnresolvedProcessing as setUserAbuseUnresolvedProcessingAction,
  getUserAbuseUnresolvedCount as getUserAbuseUnresolvedCountAction,
} from './redux/actions'
import {
  getUserAbuseReports,
  resolveUserAbuseReport,
  getUserAbuseUnresolvedCount,
} from '.'

const log = console
const storeKey = 'userAbuse'

export function initUserAbuseReportsHandler ({ after }) {
  return after([SET_AUTH_DATA, SET_PAGE_READY], async ({ state, dispatch }) => {
    const { userAbuse, page } = state
    if (!isPageReady({ page })) {
      return
    }
    if (!isFinite(userAbuse.getIn(['unresolvedCount']))) {
      dispatch(getUserAbuseUnresolvedCountAction())
    }
    if (!userAbuse.get('data')) {
      dispatch(getUserAbuseReportsAction())
    }
  })
}

export function getUserAbuseReportsHandler ({ after }) {
  return after([GET_USER_ABUSE_REPORTS], async ({ state, dispatch, action }) => {
    try {
      const { config, auth, userAbuse } = state
      const { resolved, p, pp } = action.payload
      if (!isLoggedIn(auth)) {
        return
      }
      if (userAbuse.get('processing')) {
        return
      }
      dispatch(setUserAbuseProcessingAction({ processing: true }))
      const data = await getUserAbuseReports({
        auth,
        server: getConfigDataServer(config),
        resolved,
        p,
        pp,
      })
      dispatch(setUserAbuseReportsAction({
        storeKey,
        data,
      }))
    } catch (error) {
      log.error(error, 'Error retrieving abuse reports')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}

export function getUserAbuseUnresolvedCountHandler ({ after }) {
  return after([GET_USER_ABUSE_UNRESOLVED_COUNT], async ({ state, dispatch }) => {
    try {
      const { config, auth, userAbuse } = state
      if (!isLoggedIn(auth)) {
        return
      }
      if (userAbuse.get('processingUnresolved')) {
        return
      }
      dispatch(setUserAbuseUnresolvedProcessingAction({ processing: true }))
      const options = {
        auth,
        server: getConfigDataServer(config),
      }
      const data = await getUserAbuseUnresolvedCount(options)
      dispatch(setUserAbuseUnresolvedCountAction({
        data,
      }))
    } catch (error) {
      dispatch(setUserAbuseUnresolvedProcessingAction({ processing: false }))
      log.error(error, 'Error resolving abuse report')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}

export function getUserAbuseAccusedReportsHandler ({ after }) {
  return after([GET_USER_ABUSE_ACCUSED_REPORTS], async ({ state, dispatch, action }) => {
    try {
      const { config, auth } = state
      const { accusedUid, p, pp } = action.payload
      if (!isLoggedIn(auth)) {
        return
      }
      if (!/^[0-9]+$/.test(accusedUid)) {
        return
      }
      const options = {
        auth,
        server: getConfigDataServer(config),
        accusedUid,
        p,
        pp,
      }
      const data = await getUserAbuseReports(options)
      dispatch(setUserAbuseAccusedReportsAction({
        data,
      }))
    } catch (error) {
      log.error(error, 'Error retrieving abuse reports')
      dispatch(setUserAbuseReportsErrorAction({
        error,
      }))
    }
  })
}

export function getUserAbuseAccuserReportsHandler ({ after }) {
  return after([GET_USER_ABUSE_ACCUSER_REPORTS], async ({ state, dispatch, action }) => {
    try {
      const { config, auth } = state
      const { accuserUid, p, pp } = action.payload
      if (!isLoggedIn(auth)) {
        return
      }
      if (!/^[0-9]+$/.test(accuserUid)) {
        return
      }
      const options = {
        auth,
        server: getConfigDataServer(config),
        accuserUid,
        p,
        pp,
      }
      const data = await getUserAbuseReports(options)
      dispatch(setUserAbuseAccuserReportsAction({
        storeKey,
        data,
      }))
    } catch (error) {
      log.error(error, 'Error retrieving abuse reports')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}

export function resolveUserAbuseReportHandler ({ after }) {
  return after([RESOLVE_USER_ABUSE_REPORT], async ({ state, dispatch, action }) => {
    const { config, auth } = state
    const { reportId, resolverAction } = action.payload
    try {
      if (!isLoggedIn(auth)) {
        return
      }
      const options = {
        auth,
        server: getConfigDataServer(config),
        resolverAction,
        reportId,
      }
      const data = await resolveUserAbuseReport(options)
      dispatch(setUserAbuseReportAction({
        storeKey,
        reportId,
        data,
      }))
    } catch (error) {
      log.error(error, 'Error resolving abuse report')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}

export function resolveUserAbuseAccusedReportHandler ({ after }) {
  return after([RESOLVE_USER_ABUSE_ACCUSED_REPORT], async ({ state, dispatch, action }) => {
    const { config, auth } = state
    const { reportId, resolverAction } = action.payload
    try {
      if (!isLoggedIn(auth)) {
        return
      }
      const options = {
        auth,
        server: getConfigDataServer(config),
        resolverAction,
        reportId,
      }
      const data = await resolveUserAbuseReport(options)
      dispatch(setUserAbuseAccusedReportAction({
        storeKey,
        reportId,
        data,
      }))
    } catch (error) {
      log.error(error, 'Error resolving abuse report')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}

export function resolveUserAbuseAccuserReportHandler ({ after }) {
  return after([RESOLVE_USER_ABUSE_ACCUSER_REPORT], async ({ state, dispatch, action }) => {
    try {
      const { config, auth } = state
      const { reportId, resolverAction } = action.payload
      if (!isLoggedIn(auth)) {
        return
      }
      const options = {
        auth,
        server: getConfigDataServer(config),
        resolverAction,
        reportId,
      }
      const data = await resolveUserAbuseReport(options)
      dispatch(setUserAbuseAccuserReportAction({
        storeKey,
        reportId,
        data,
      }))
    } catch (error) {
      log.error(error, 'Error resolving abuse report')
      dispatch(setUserAbuseReportsErrorAction({
        storeKey,
        error,
      }))
    }
  })
}
